import React, {
  ReactNode, useEffect, useMemo, useState,
} from "react"
import useModal from "services/useModal"
import {
  Account, AccountConfiguration, DioboxEvent, ModalName, TextMessage,
} from "sharedTypes"
import pluralize from "pluralize"
import { Box, Text } from "@chakra-ui/react"
import EventPage, { EventPageHeader } from "components/layout/EventPage"
import HorizontalStat from "components/elements/HorizontalStat"
import Button from "components/Buttons/Button"
import EventResourcesView from "components/layout/EventResourcesView"
import EventResourcesViewListItem from "components/EventResourcesViewListItem"
import OptInMsgIcon from "components/elements/OptInMsgIcon"
import Popover from "components/dialogs/Popover"
import SelectPrompt from "components/SelectPrompt"
import { useParams } from "react-router-dom"
import { eventTextMessagePath } from "utilities/routes"
import { asDate } from "utilities/strings"
import { usePaginatedTextMessages } from "queries/texts"
import Spinner from "components/elements/Spinner"
import { useIntercom } from "react-use-intercom"
import useSearchParams from "services/useSearchParams"
import BuyTextMessageCredits from "modals/TextMessage/TextMessageCredits/BuyTextMessageCredits"
import TextChannelIcon from "components/elements/TextChannelIcon"
import useFeatureFlag from "services/featureFlags"
import useOptInContentSids from "services/useOptInContentSids"
import NewTextMenu from "./NewTextMenu"
import NoTexts from "./NoTexts"
import SelectedTextMessage from "./SelectedTextMessage/SelectedTextMessage"
import PendingTextingVerificationAlert from "./PendingTextingVerificationAlert"
import CreditCounter from "./CreditCounter"

type Props = {
  event: DioboxEvent
  emailEnabled: boolean
  smsCredits: number
  currentAccount: Account
  accountConfiguration: AccountConfiguration
}

const Texts = ({
  event: {
    counts: {
      texts: textCount,
    },
    textsEnabled,
  },
  event,
  emailEnabled,
  smsCredits,
  currentAccount,
  accountConfiguration: { smsCreditPackages },
}: Props) => {
  const { eventId, textMessageId } = useParams<{ eventId: string, textMessageId: string }>()
  const showModal = useModal()
  const { isLoading, fetchNextPage, texts } = usePaginatedTextMessages(eventId)
  const { show: showIntercom } = useIntercom()
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const { showPaymentSuccessModal } = useSearchParams()
  const { whatsappMessaging: whatsAppEnabled } = useFeatureFlag()

  const optInContentSids = useOptInContentSids()
  // Group texts into three sections rendered top-to-bottom with section
  // headers. List is paginated, so items in later pages slot into their
  // section as they load — acceptable since the section never reorders,
  // it just grows.
  const sections = useMemo(() => {
    const empty: Record<"optIn" | "whatsapp" | "sms", typeof texts> = {
      optIn: [], whatsapp: [], sms: [],
    }
    if (!texts) return empty
    const channelFiltered = whatsAppEnabled
      ? texts
      : texts.filter((t) => t.channel !== "whatsapp")
    const result: typeof empty = { optIn: [], whatsapp: [], sms: [] }
    channelFiltered.forEach((t) => {
      const sid = t.whatsappContentSid
      if (sid !== undefined && optInContentSids.has(sid)) {
        result.optIn.push(t)
      } else if (t.channel === "whatsapp") {
        result.whatsapp.push(t)
      } else {
        result.sms.push(t)
      }
    })

    return result
  }, [texts, whatsAppEnabled, optInContentSids])

  useEffect(() => {
    if (showPaymentSuccessModal) {
      showModal(ModalName.PaymentSuccessful)
    }
  }, [])

  if (isLoading) {
    return <Spinner />
  }

  if (textCount === 0) {
    return (
      <NoTexts
        emailEnabled={emailEnabled}
        textsEnabled={textsEnabled}
      />
    )
  }

  const baseRowOptions: SectionRowOptions = {
    eventId,
    textMessageId,
    defaultOptInTextId: event.defaultOptInTextId,
  }
  const listItems = whatsAppEnabled
    ? [
      ...renderSection("WhatsApp Opt-In", sections.optIn, { ...baseRowOptions, hideDateAndStatus: true }),
      ...renderSection("WhatsApp Messages", sections.whatsapp, baseRowOptions),
      ...renderSection("SMS Messages", sections.sms, baseRowOptions),
    ]
    : sections.sms.map((text) => renderRow(text, baseRowOptions))

  return (
    <>
      <EventPage
        scrollableBody={false}
        header={(
          <EventPageHeader
            icon="texts-nav"
            title="Texts"
            items={(
              <>
                <HorizontalStat label={pluralize("Message", textCount)} value={textCount} />
                {textsEnabled && (
                  <CreditCounter
                    smsCredits={smsCredits}
                    onClickAddCredits={() => setShowPaymentModal(true)}
                  />
                )}
                <NewTextMenu
                  button={(
                    <Button leftIcon="plus" size="sm">
                      Text Message
                    </Button>
                  )}
                />
              </>
            )}
          />
        )}
        body={(
          <EventResourcesView
            onScrollEnd={fetchNextPage}
            listHeader={
            !emailEnabled ? (
              <Box p={4}>
                <PendingTextingVerificationAlert contactUs={showIntercom} />
              </Box>
            ) : null
          }
            listItems={listItems}
            showBorder={false}
            bgColor={textMessageId ? "gray.50" : "white"}
            selectedItem={textMessageId ? (
              <SelectedTextMessage
                textMessageId={textMessageId}
                event={event}
                enabled={textsEnabled}
                emailEnabled={emailEnabled}
                smsCredits={smsCredits}
                currentAccount={currentAccount}
                onClickAddCredits={() => setShowPaymentModal(true)}
              />
            ) : <SelectPrompt text="Select a Text Message" />}
          />
        )}
      />
      {showPaymentModal && (
        <BuyTextMessageCredits
          eventId={eventId}
          smsCreditPackages={smsCreditPackages}
          onHide={() => setShowPaymentModal(false)}
        />
      )}
    </>
  )
}

type SectionRowOptions = {
  eventId: string
  textMessageId: string
  defaultOptInTextId: number | null
  hideDateAndStatus?: boolean
}

const renderSection = (
  title: string,
  items: ReadonlyArray<TextMessage>,
  options: SectionRowOptions,
): ReactNode[] => {
  if (items.length === 0) return []

  return [
    <SectionHeader key={`header-${title}`} title={title} />,
    ...items.map((text) => renderRow(text, options)),
  ]
}

const renderRow = (
  {
    id, externalId, name, sentAt, channel, updatedAt,
  }: TextMessage,
  {
    eventId, textMessageId, defaultOptInTextId, hideDateAndStatus,
  }: SectionRowOptions,
): ReactNode => {
  const isDefaultOptIn = id !== undefined && defaultOptInTextId === id

  return (
    <EventResourcesViewListItem
      key={externalId}
      id={externalId!}
      selectedListItemId={textMessageId}
      path={eventTextMessagePath(eventId, externalId!)}
      icon={<TextChannelIcon channel={channel} size={8} />}
      title={name!}
      subtitle={hideDateAndStatus ? null : asDate(updatedAt)}
      sentOn={sentAt ?? null}
      hideStatus={hideDateAndStatus}
      endIcon={isDefaultOptIn ? (
        <Popover
          placement="left"
          triggerEvent="hover"
          contentWidth="xs"
          trigger={<OptInMsgIcon boxSize={5} color="whatsapp.500" cursor="help" />}
          body={(
            <Box p={3}>
              <Text fontSize="sm">
                Guests who haven&apos;t opted in will receive this opt-in
                message first. If they approve within 48 hours, your
                message is sent automatically.
              </Text>
            </Box>
          )}
        />
      ) : null}
    />
  )
}

// Mirrors the gray-bg header used in Popover (e.g. the freeform variable
// editor in the WhatsApp template editor). Border-top comes from the row
// layout above; we only add border-bottom to mark the divider into rows.
const SectionHeader = ({ title }: { title: string }) => (
  <Box bgColor="gray.100" px={5} py={2} borderBottomWidth={1}>
    <Text fontSize="md" fontWeight="semibold" color="black">{title}</Text>
  </Box>
)

export default Texts
